import { useSearchParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useState } from "react";
import { Loader2, FileText, Calendar, User, Shield } from "lucide-react";
import { SignatureCapture } from "@/components/documents/SignatureCapture";
import { NativePdfPreview } from "@/components/documents/NativePdfPreview";

interface SignatureRequestData {
  id: string;
  una_id: string;
  document_id: string;
  requested_from: string;
  created_by: string;
  signing_token: string;
  role_signing_as: string;
  capacity: string;
  signature_type: string;
  status: string;
  expires_at: string;
  completed_at: string | null;
  signed_by: string | null;
  document: {
    id: string;
    document_name: string;
    file_path: string | null;
    file_url: string | null;
  };
  una: {
    name: string;
  };
  requested_by_profile?: {
    full_name: string;
  };
}

export default function SignDocument() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  const [showSignature, setShowSignature] = useState(false);

  // Validate token and load signature request via edge function (bypasses RLS)
  const { data: request, isLoading, error } = useQuery<SignatureRequestData>({
    queryKey: ["signature-request", token],
    queryFn: async () => {
      if (!token) throw new Error("No signing token provided");

      // Use edge function to bypass RLS and validate token
      const { data, error } = await supabase.functions.invoke(
        "get-signature-request-by-token",
        {
          body: { signingToken: token },
        }
      );

      if (error) {
        console.error('Edge function error:', error);
        throw new Error(error.message || 'Failed to fetch signature request');
      }

      if (data?.error) {
        console.error('Signature request error:', data.error);
        throw new Error(data.error);
      }

      return data as SignatureRequestData;
    },
    enabled: !!token,
    retry: false,
  });

  // Handle signature submission via edge function (bypasses RLS for token-based signing)
  const signatureMutation = useMutation({
    mutationFn: async (signatureData: string) => {
      if (!token) {
        throw new Error("Missing signing token");
      }

      // Call edge function with service role to bypass RLS
      const { data, error } = await supabase.functions.invoke(
        "submit-member-signature",
        {
          body: {
            signingToken: token,
            signatureData: signatureData,
          },
        }
      );

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      return data;
    },
    onSuccess: () => {
      toast.success("Document signed successfully!");
      setTimeout(() => {
        navigate("/governance/tasks?view=assigned");
      }, 2000);
    },
    onError: (error: Error) => {
      toast.error(`Failed to sign document: ${error.message}`);
    },
  });

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading signature request...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !request) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <div className="max-w-md w-full bg-card rounded-lg shadow-lg p-8 text-center border">
          <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <FileText className="w-8 h-8 text-destructive" />
          </div>
          <h1 className="text-2xl font-semibold mb-2">
            Invalid Signature Request
          </h1>
          <p className="text-muted-foreground mb-6">
            This signature link is no longer valid. It may have expired or already been used.
          </p>
          <Button onClick={() => navigate("/governance/tasks?view=assigned")} className="w-full">
            Return to My Tasks
          </Button>
        </div>
      </div>
    );
  }

  const expiresDate = new Date(request.expires_at);
  const daysUntilExpiry = Math.ceil((expiresDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24));

  return (
    <div className="min-h-screen bg-background py-4 sm:py-8 px-3 sm:px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-card rounded-lg shadow-sm p-4 sm:p-6 mb-4 sm:mb-6 border">
          <h1 className="text-2xl sm:text-3xl font-semibold mb-4">
            Sign Document
          </h1>
          
          {/* Document Details Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-sm font-medium text-muted-foreground">UNA</p>
                <p className="text-sm sm:text-base truncate">{request.una.name}</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <FileText className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-sm font-medium text-muted-foreground">Document</p>
                <p className="text-sm sm:text-base break-words">{request.document.document_name}</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <User className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-sm font-medium text-muted-foreground">Signing As</p>
                <p className="text-sm sm:text-base">{request.capacity}</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Calendar className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-sm font-medium text-muted-foreground">Expires</p>
                <p className="text-sm sm:text-base">
                  {expiresDate.toLocaleDateString()} ({daysUntilExpiry} days)
                </p>
              </div>
            </div>
          </div>

          <div className="mt-4 pt-4 border-t">
            <p className="text-sm text-muted-foreground">
              Requested by <span className="font-medium text-foreground">{request.requested_by_profile?.full_name}</span>
            </p>
          </div>
        </div>

        {/* Document Preview */}
        <div className="bg-card rounded-lg shadow-sm p-4 sm:p-6 mb-4 sm:mb-6 border">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">Document Preview</h2>
            {request.document.file_url && (
              <Button
                variant="outline"
                size="sm"
                className="sm:hidden min-h-[44px]"
                onClick={() => window.open(request.document.file_url!, '_blank')}
              >
                View Full
              </Button>
            )}
          </div>
          <div className="border rounded-lg overflow-hidden bg-muted min-h-[300px] sm:min-h-[400px]">
            {request.document.file_url ? (
              <NativePdfPreview
                url={request.document.file_url}
                maxWidth={900}
                heightClassName="h-[350px] sm:h-[600px]"
              />
            ) : (
              <div className="flex items-center justify-center h-[350px] sm:h-[600px]">
                <p className="text-muted-foreground">Document preview not available</p>
              </div>
            )}
          </div>
        </div>

        {/* Signature Section */}
        {!showSignature ? (
          <div className="bg-card rounded-lg shadow-sm p-4 sm:p-6 border">
            <div className="text-center">
              <p className="text-sm sm:text-base text-foreground mb-6">
                By clicking "Proceed to Sign", you confirm that you have reviewed the document and agree to sign as <strong>{request.capacity}</strong>.
              </p>
              <Button
                onClick={() => setShowSignature(true)}
                size="lg"
                className="px-8 w-full sm:w-auto min-h-[48px]"
              >
                Proceed to Sign
              </Button>
            </div>
          </div>
        ) : (
          <div className="bg-card rounded-lg shadow-sm p-4 sm:p-6 border">
            <h2 className="text-lg font-semibold mb-4">
              Draw Your Signature
            </h2>
            <SignatureCapture
              role="president"
              signerName={request.capacity}
              onSave={(signatureData) => signatureMutation.mutate(signatureData)}
              onCancel={() => setShowSignature(false)}
              isLoading={signatureMutation.isPending}
            />
          </div>
        )}
      </div>
    </div>
  );
}
